import type { ComponentProps, ReactNode } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Card from "@/v2-ui/card"

export type UsageReportKpiProps = Omit<ComponentProps<typeof Card>, "title"> & {
  /** Small uppercase label ("Tool calls"). */
  label: ReactNode
  /** The big number, with an optional badge beside it. */
  value: ReactNode
  /** One or two short context lines under the number ("30 % of the calls"). */
  sub?: ReactNode | ReactNode[]
  /** Amber label and edge for a number that is a warning (refusals). */
  tone?: "default" | "warning"
}

/**
 * A key figure as the report shows one: its own card, label on top, the
 * number large, one or two lines of context under it. Tiles sit in a
 * `usage-report-kpis` grid.
 */
function UsageReportKpi(props: UsageReportKpiProps) {
  const { label, value, sub, tone = "default", className, ...cardProps } = props
  const subLines = (Array.isArray(sub) ? sub : [sub]).filter(
    (line) => line != null && line !== false && line !== "",
  )

  return (
    <Card
      className={cn(
        "usage-report-kpi",
        tone === "warning" && "usage-report-kpi--warning",
        className,
      )}
      {...cardProps}
    >
      <span className="usage-report-kpi-label">{label}</span>
      <span className="usage-report-kpi-value">{value}</span>
      {subLines.length > 0 && (
        <span className="usage-report-kpi-subs">
          {subLines.map((line, index) => (
            <span key={index} className="usage-report-kpi-sub">
              {line}
            </span>
          ))}
        </span>
      )}
    </Card>
  )
}

export default UsageReportKpi
